/* Admin → Save & Email (0.3e Stage B): whether email is on, today's emails against the cap, the last
   sending problem, and the staff lookup by verified email (read-only, logged with the staff member's
   name). Support actions: Clear code pause, Sign out all devices, Forget this email (typed twice). */

import { useEffect, useState } from 'react';
import { formatMoney } from '@gsb/core';
import { api } from '../api';
import { Icon } from '../ui/icons';
import { Dialog } from '../ui/primitives';

const NAME_KEY = 'gsb:staff-name';
const when = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString('en-NZ', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : '—');
const ACTIONS: Record<string, string> = {
  staff_lookup: 'Looked up', staff_clear_pause: 'Cleared the code pause', staff_signout_all: 'Signed out all devices', staff_forget: 'Forgot the email'
};

export function EmailAdmin() {
  const [st, setSt] = useState<any>(null);
  const [audit, setAudit] = useState<any[]>([]);
  const [name, setName] = useState(() => { try { return localStorage.getItem(NAME_KEY) || ''; } catch { return ''; } });
  const [email, setEmail] = useState('');
  const [res, setRes] = useState<any>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [forget, setForget] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState('');
  const refresh = () => {
    api.adminEmailStatus().then(setSt).catch(() => {});
    api.adminEmailAudit().then(r => setAudit(r.entries)).catch(() => {});
  };
  useEffect(refresh, []);

  const run = async (action: 'lookup' | 'clear-pause' | 'signout-all' | 'forget', extra: { confirmEmail?: string } = {}) => {
    setErr(''); setBusy(true);
    try { localStorage.setItem(NAME_KEY, name.trim()); } catch { /* ignore */ }
    try {
      const r = await api.adminEmail(action, { email: email.trim(), staffName: name.trim(), ...extra });
      if (action === 'lookup') setRes(r);
      else setRes(await api.adminEmail('lookup', { email: email.trim(), staffName: name.trim() }));
      if (action === 'forget') { setForget(false); setConfirmEmail(''); }
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); refresh(); }
  };

  const mode = st?.mode as 'off' | 'staff' | 'on' | undefined;
  return (
    <section className="card adm-email" style={{ padding: 20, marginTop: 16 }}>
      <h2 className="h3" style={{ marginBottom: 6 }}>Save &amp; Email My Gangsheet</h2>
      {st && (
        <>
          <div className="adm-email-grid">
            <div><span className="label">Status</span><b>{mode === 'on' ? 'On for everyone' : mode === 'staff' ? 'Staff only' : 'Off'}</b>
              <div className="small muted">EMAIL_RECOVERY={st.setting}{st.setting === 'on' && !st.privacyUrlSet ? ' (staff only until PRIVACY_URL is set)' : ''}</div></div>
            <div><span className="label">Emails today (UTC)</span><b className="num">{st.today.sent} of {st.today.cap}</b>
              <div className="small muted">{st.today.blocked ? 'Resend’s quota is used up: paused until ' + st.today.resumesAt : 'confirmations stop at ' + (st.today.cap - st.today.confirmReserve)}</div></div>
            <div><span className="label">Sending</span><b>{st.transport === 'resend' ? (st.resendKeySet ? 'Resend' : 'Resend: no key set') : 'Captured (not sent)'}</b>
              <div className="small muted">{st.from} · replies to {st.replyTo}</div></div>
          </div>
          {st.lastError && <div className="callout callout-warn" style={{ marginTop: 10 }}><Icon name="alert" /><span>Last problem sending ({when(st.lastError.at)}, {st.lastError.kind}): {st.lastError.detail}</span></div>}
        </>
      )}

      <h3 className="h3" style={{ margin: '18px 0 4px', fontSize: 15 }}>Find a customer’s gangsheets</h3>
      <p className="tiny muted">Read-only. Every lookup and action is logged with your name. Customers sign in only with their own code; staff can’t sign in as a customer.</p>
      <form className="adm-email-form" onSubmit={e => { e.preventDefault(); void run('lookup'); }}>
        <label className="field"><span className="label">Your name (for the log)</span><input className="in" value={name} maxLength={60} onChange={e => setName(e.target.value)} data-adm="staff-name" /></label>
        <label className="field"><span className="label">Customer’s email</span><input className="in" type="email" value={email} onChange={e => { setEmail(e.target.value); setRes(null); }} data-adm="email" /></label>
        <button className="btn btn-primary btn-sm" disabled={busy || !name.trim() || !email.trim()} data-adm="lookup"><Icon name="search" />Look up</button>
      </form>
      {err && <p className="callout callout-bad" style={{ marginTop: 10 }}><Icon name="alert" /><span>{err}</span></p>}

      {res && (
        <div className="adm-email-res">
          {!res.found ? (
            <p className="small">No verified email {res.email}. {res.paused ? 'Codes for it are paused (10 wrong tries in 24 hours).' : ''} {res.codesToday ? res.codesToday + ' code(s) asked for in the last 24 hours.' : ''}</p>
          ) : (
            <>
              <p className="small">
                <b>{res.email}</b> · verified {when(res.verifiedAt)} · last code {when(res.lastVerifiedAt)} · {res.activeSessions} device{res.activeSessions === 1 ? '' : 's'} signed in
                {res.paused ? <> · <span className="chip chip-red">Codes paused</span></> : null}
              </p>
              {!res.gangsheets.length ? <p className="small muted">No gangsheets saved to it.</p> : (
                <ul className="adm-email-list">
                  {res.gangsheets.map((g: any) => (
                    <li key={g.id}>
                      <img src={g.thumbUrl} alt="" loading="lazy" onError={e => ((e.target as HTMLImageElement).style.visibility = 'hidden')} />
                      <div>
                        <b>{g.name}</b>
                        <div className="tiny muted">{g.transfer === 'uv' ? 'UV DTF' : 'DTF'} · {g.sheets} sheet{g.sheets === 1 ? '' : 's'}{g.price != null ? ' · ' + formatMoney(g.price) : ''} · edited {when(g.updatedAt)}</div>
                        <div className="tiny">{g.orderedAt ? <>Ordered {when(g.orderedAt)}{g.orderRef ? ' · ' + g.orderRef : ''}{g.staffUrl ? <> · <a className="link small" href={g.staffUrl} target="_blank" rel="noreferrer">Job page</a></> : null}</> : g.status === 'submitted' ? 'Sent to order (not finished)' : 'Draft'}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
          <div className="em-row" style={{ marginTop: 12 }}>
            {res.paused && <button className="btn btn-line btn-sm" disabled={busy} onClick={() => void run('clear-pause')} data-adm="clear">Clear code pause</button>}
            {res.found && res.activeSessions > 0 && <button className="btn btn-line btn-sm" disabled={busy} onClick={() => void run('signout-all')} data-adm="signout-all"><Icon name="signOut" />Sign out all devices</button>}
            {res.found && <button className="btn btn-danger btn-sm" disabled={busy} onClick={() => setForget(true)} data-adm="forget">Forget this email…</button>}
          </div>
        </div>
      )}

      {audit.length > 0 && (
        <details className="adm-email-audit">
          <summary className="small">Staff log ({audit.length})</summary>
          <ul className="tiny">
            {audit.map((a, i) => <li key={i}>{when(a.at)} · <b>{a.staff}</b> · {ACTIONS[a.action] || a.action} {a.detail?.email || ''}{a.detail?.found === false ? ' (not found)' : ''}</li>)}
          </ul>
        </details>
      )}

      {forget && res?.found && (
        <Dialog title="Forget this email" onClose={() => setForget(false)}>
          <span className="kicker">Deletion request</span>
          <h2>Forget {res.email}?</h2>
          <p className="small muted">Every device signed in to it is signed out, it comes off all {res.gangsheets.length} gangsheet{res.gangsheets.length === 1 ? '' : 's'} saved to it, its code requests are deleted, and the stored address is replaced. Each gangsheet stays with the browser that made it. <b>No gangsheet is deleted</b>, and orders, print files and order references aren’t touched. This can’t be undone.</p>
          <label className="field" style={{ marginTop: 10 }}><span className="label">Type the email again to confirm</span><input className="in" type="email" value={confirmEmail} onChange={e => setConfirmEmail(e.target.value)} data-adm="confirm-email" /></label>
          <div className="dialog-actions">
            <button className="btn btn-line btn-sm" onClick={() => setForget(false)}>Cancel</button>
            <button className="btn btn-danger btn-sm" disabled={busy || confirmEmail.trim().toLowerCase() !== res.email} onClick={() => void run('forget', { confirmEmail })} data-adm="forget-confirm">Forget this email</button>
          </div>
        </Dialog>
      )}
    </section>
  );
}
